import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDiscreet } from "@/context/DiscreetContext";
import { SeoHead } from "@/components/SeoHead";
import {
  Search, Sparkles, Brain, Activity, Clock, TrendingUp, TrendingDown,
  Minus, Filter, ChevronDown, ChevronRight, AlertCircle, BookOpen,
} from "lucide-react";

// Shared blur wrapper — every screen honours the Discreet toggle.
function Maybe({ children, className = "" }) {
  const { discreet } = useDiscreet();
  return <span className={`${discreet ? "discreet-blur" : ""} ${className}`}>{children}</span>;
}

const EVENT_TYPE_OPTIONS = [
  { value: "", label: "All event types" },
  { value: "promise_made", label: "Promise made" },
  { value: "promise_broken", label: "Promise broken" },
  { value: "support_escalation", label: "Support escalation" },
  { value: "support_withdrawal", label: "Support withdrawal" },
  { value: "communication_slowdown", label: "Communication slowdown" },
  { value: "communication_improvement", label: "Communication improvement" },
  { value: "reassurance_cycle", label: "Reassurance cycle" },
  { value: "emotional_withdrawal", label: "Emotional withdrawal" },
  { value: "conflict_event", label: "Conflict" },
  { value: "reconciliation", label: "Reconciliation" },
  { value: "exclusivity_discussion", label: "Exclusivity discussion" },
  { value: "attachment_escalation", label: "Attachment escalation" },
  { value: "attachment_distance", label: "Attachment distance" },
  { value: "gifting_shift", label: "Gifting shift" },
  { value: "consistency_improvement", label: "Consistency improvement" },
  { value: "unresolved_issue", label: "Unresolved issue" },
  { value: "boundary_test", label: "Boundary test" },
  { value: "future_commitment", label: "Future commitment" },
  { value: "future_commitment_broken", label: "Future commitment broken" },
  { value: "intimacy_milestone", label: "Intimacy milestone" },
  { value: "withdrawal_recurrence", label: "Withdrawal recurrence" },
  { value: "transparency_moment", label: "Transparency moment" },
  { value: "evasion_moment", label: "Evasion moment" },
  { value: "trust_breach", label: "Trust breach" },
  { value: "trust_repair", label: "Trust repair" },
];

function ValenceIcon({ valence }) {
  if (valence === "positive") return <TrendingUp size={14} className="text-sage" />;
  if (valence === "negative") return <TrendingDown size={14} className="text-mauve" />;
  return <Minus size={14} className="text-cocoa/50" />;
}

function ScoreBar({ score }) {
  const pct = Math.round((score || 0) * 100);
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="h-1.5 flex-1 rounded-full bg-champagne overflow-hidden">
        <div className="h-full bg-mauve rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] text-cocoa/55 tabular-nums w-8 text-right">{pct}%</span>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}

function fmtType(t) {
  if (!t) return "";
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function Intelligence() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("");
  const [overview, setOverview] = useState(null);
  const [observations, setObservations] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventType, setEventType] = useState("");
  const [valence, setValence] = useState("");
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;
  const [summaries, setSummaries] = useState([]);
  const [openSummary, setOpenSummary] = useState(null);
  const [openObs, setOpenObs] = useState(null);

  // Search state
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchMeta, setSearchMeta] = useState(null);
  const [searchedQ, setSearchedQ] = useState("");

  const loadAll = useCallback(async () => {
    const params = profileId ? { profile_id: profileId } : {};
    const [ov, cont, sums] = await Promise.all([
      api.get("/intelligence/overview", { params }).then((r) => r.data).catch(() => null),
      api.get("/intelligence/continuity", { params }).then((r) => r.data?.observations || []).catch(() => []),
      api.get("/intelligence/summary/history", { params: { ...params, limit: 20 } }).then((r) => r.data?.summaries || []).catch(() => []),
    ]);
    setOverview(ov);
    setObservations(cont);
    setSummaries(sums);
  }, [profileId]);

  const loadEvents = useCallback(async () => {
    const params = {
      limit: PAGE_SIZE,
      skip: page * PAGE_SIZE,
      sort,
    };
    if (profileId) params.profile_id = profileId;
    if (eventType) params.event_type = eventType;
    if (valence) params.valence = valence;
    const r = await api.get("/intelligence/events", { params }).then((rr) => rr.data).catch(() => ({ events: [], total: 0 }));
    setEvents(r.events || []);
    setEventsTotal(r.total || 0);
  }, [profileId, page, sort, eventType, valence]);

  useEffect(() => {
    api.get("/profiles").then((r) => setProfiles(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { setPage(0); }, [profileId, eventType, valence, sort]);

  const runSearch = async (term) => {
    const t = (term ?? q).trim();
    if (!t) {
      setSearchResults([]); setSearchMeta(null); setSearchedQ("");
      return;
    }
    setSearching(true);
    try {
      const params = { q: t, k: 15 };
      if (profileId) params.profile_id = profileId;
      const r = await api.get("/intelligence/search", { params }).then((rr) => rr.data);
      setSearchResults(r?.results || []);
      setSearchMeta(r?.counts || null);
      setSearchedQ(t);
    } catch {
      setSearchResults([]); setSearchMeta(null);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => { setQ(""); setSearchResults([]); setSearchMeta(null); setSearchedQ(""); };

  const totalPages = Math.max(1, Math.ceil(eventsTotal / PAGE_SIZE));
  const aliasOf = (pid) => profiles.find((p) => p.id === pid)?.alias || "—";

  return (
    <div data-testid="intelligence-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <SeoHead title="Your relationship intelligence · SugarSense" description="Search, review, and revisit every pattern SugarSense has tracked for you." />

      {/* ─── Hero ─── */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Intelligence</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-cocoa mt-1 leading-tight">
            Everything you've shared. <span className="italic">Searchable.</span>
          </h1>
          <p className="mt-3 text-cocoa/70 text-base max-w-2xl">
            Your relationship history isn't a chat log. It's a structured intelligence record — events,
            patterns, summaries — all yours, all private, all searchable by what you actually mean.
          </p>
        </div>
        <Link to="/ask">
          <button data-testid="intel-ask-btn" className="ss-pill ss-pill-gold h-11"><Sparkles size={14} /> Ask SugarSense</button>
        </Link>
      </div>

      {/* ─── Profile filter ─── */}
      {profiles.length > 0 && (
        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <Filter size={14} className="text-cocoa/60" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-cocoa/60">Scope</span>
          <select
            data-testid="intel-profile-filter"
            value={profileId}
            onChange={(e) => setProfileId(e.target.value)}
            className="bg-ivory border border-champagne rounded-full h-9 px-4 text-sm text-cocoa outline-none focus:border-mauve"
          >
            <option value="">Everything you've tracked</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>{p.alias}</option>
            ))}
          </select>
        </div>
      )}

      {/* ─── KPI tiles ─── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        <KPI label="Structured events" value={overview?.total_events ?? "—"} icon={<Activity size={14} />} testid="intel-kpi-events" />
        <KPI label="Active patterns" value={overview?.active_patterns ?? "—"} icon={<Brain size={14} />} testid="intel-kpi-patterns" />
        <KPI label="Latest summary"
          value={overview?.latest_summary ? `v${overview.latest_summary.version}` : "—"}
          sub={overview?.latest_summary ? fmtDate(overview.latest_summary.generated_at) : ""}
          icon={<BookOpen size={14} />} testid="intel-kpi-summary" />
        <KPI label="Emotional weight"
          value={overview?.valence_breakdown
            ? `+${overview.valence_breakdown.positive} / -${overview.valence_breakdown.negative}`
            : "—"}
          sub={overview?.valence_breakdown ? `${overview.valence_breakdown.neutral} neutral` : ""}
          icon={<TrendingUp size={14} />} testid="intel-kpi-valence" />
      </div>

      {/* ─── Intelligent search ─── */}
      <section className="ss-card-premium p-5 sm:p-6 mb-10" data-testid="intel-search-card">
        <p className="text-[10px] tracking-[0.4em] uppercase text-mauve mb-2">Intelligent search</p>
        <p className="font-heading italic text-xl sm:text-2xl text-cocoa mb-4">Ask in plain language. We'll find the pattern.</p>
        <form
          onSubmit={(e) => { e.preventDefault(); runSearch(); }}
          className="flex items-center gap-2"
        >
          <div className="flex items-center gap-2 flex-1 min-w-0 bg-ivory border border-champagne rounded-full h-12 px-4 focus-within:border-mauve transition-colors">
            <Search size={16} className="text-cocoa/60 shrink-0" />
            <input
              data-testid="intel-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='e.g. "when he pulled away after I asked about the weekend"'
              className="flex-1 min-w-0 bg-transparent outline-none text-cocoa text-[15px] placeholder:text-cocoa/40"
            />
            {q && (
              <button type="button" onClick={clearSearch} data-testid="intel-search-clear"
                className="text-cocoa/50 text-sm px-2 hover:text-cocoa">Clear</button>
            )}
          </div>
          <button type="submit" data-testid="intel-search-submit"
            disabled={searching || !q.trim()}
            className="ss-pill ss-pill-gold h-12 px-5 shrink-0 disabled:opacity-40">
            {searching ? "Searching…" : "Search"}
          </button>
        </form>
        <p className="text-[11px] text-cocoa/55 mt-3">
          Combines semantic recall (over your private embeddings) with keyword matching across events &amp; summaries.
          Everything stays inside your account.
        </p>

        {searchedQ && (
          <div className="mt-6" data-testid="intel-search-results">
            <p className="text-[11px] tracking-[0.3em] uppercase text-cocoa/60 mb-3">
              {searchResults.length === 0
                ? `No matches for "${searchedQ}"`
                : `${searchResults.length} match${searchResults.length === 1 ? "" : "es"} for "${searchedQ}"`}
              {searchMeta?.semantic > 0 && <span className="ml-2 text-mauve">· {searchMeta.semantic} semantic</span>}
              {(searchMeta?.keyword_events || 0) + (searchMeta?.keyword_summaries || 0) > 0 && (
                <span className="ml-2 text-mauve">· {(searchMeta.keyword_events + searchMeta.keyword_summaries)} keyword</span>
              )}
            </p>
            <ul className="space-y-2">
              {searchResults.map((r) => (
                <li key={`${r.kind}-${r.id}`}
                  data-testid={`intel-result-${r.kind}-${r.id}`}
                  className="ss-card p-4 hover:border-mauve transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-mauve">{r.kind.replace("_", " ")}</span>
                        {r.meta?.valence && <ValenceIcon valence={r.meta.valence} />}
                        {r.match_kinds?.map((mk) => (
                          <span key={mk} className="text-[10px] uppercase tracking-wide text-cocoa/55 bg-champagne px-2 py-0.5 rounded-full">{mk}</span>
                        ))}
                        {r.meta?.profile_id && (
                          <span className="text-[10px] uppercase tracking-wide text-cocoa/55"><Maybe>{aliasOf(r.meta.profile_id)}</Maybe></span>
                        )}
                      </div>
                      <p className="font-medium text-cocoa text-sm"><Maybe>{r.title}</Maybe></p>
                      <Maybe className="block mt-1 text-cocoa/80 text-[13px] leading-relaxed line-clamp-3">{r.preview}</Maybe>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[11px] text-cocoa/55">{fmtDate(r.occurred_at)}</p>
                      <div className="mt-1"><ScoreBar score={r.score} /></div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* ─── Active patterns ─── */}
      <section className="mb-10" data-testid="intel-patterns-section">
        <div className="flex items-end justify-between mb-3">
          <div>
            <p className="text-[10px] tracking-[0.4em] uppercase text-mauve">Continuity</p>
            <h2 className="font-heading text-2xl sm:text-3xl text-cocoa">Recurring patterns we're tracking.</h2>
          </div>
          <span className="text-[11px] text-cocoa/55">{observations.length} active</span>
        </div>
        {observations.length === 0 ? (
          <div className="ss-card p-6 text-cocoa/60 text-sm">No active patterns yet. They surface after a few advisor sessions.</div>
        ) : (
          <ul className="space-y-2">
            {observations.map((o) => {
              const open = openObs === o.observation_key;
              return (
                <li key={o.observation_key}
                  data-testid={`intel-pattern-${o.observation_key}`}
                  className="ss-card overflow-hidden">
                  <button onClick={() => setOpenObs(open ? null : o.observation_key)}
                    className="w-full text-left p-4 flex items-center gap-3 hover:bg-champagne/30">
                    {open ? <ChevronDown size={16} className="text-cocoa/60" /> : <ChevronRight size={16} className="text-cocoa/60" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-cocoa text-sm">{o.human_label}</p>
                      <p className="text-[11px] text-cocoa/55 mt-0.5">
                        {o.occurrences}× · first seen {fmtDate(o.first_seen_at)} · last {fmtDate(o.last_seen_at)}
                      </p>
                    </div>
                    <ScoreBar score={o.confidence} />
                  </button>
                  {open && (
                    <div className="px-5 pb-4 pt-1 border-t border-champagne text-[13px] text-cocoa/75 leading-relaxed">
                      <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55 mb-2">Evidence — {(o.evidence_event_ids || []).length} event{(o.evidence_event_ids || []).length === 1 ? "" : "s"}</p>
                      <p className="text-cocoa/70">Emotional weight {Math.round((o.emotional_weight || 0) * 100)}% · confidence {Math.round((o.confidence || 0) * 100)}%.</p>
                      <p className="text-cocoa/55 mt-2 text-[12px] italic">Patterns are detected by deterministic platform logic — not by the LLM. They survive provider swaps.</p>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ─── Event timeline ─── */}
      <section className="mb-10" data-testid="intel-timeline-section">
        <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="text-[10px] tracking-[0.4em] uppercase text-mauve">Timeline</p>
            <h2 className="font-heading text-2xl sm:text-3xl text-cocoa">Every event, every entry.</h2>
          </div>
          <span className="text-[11px] text-cocoa/55">{eventsTotal} total · page {page + 1} of {totalPages}</span>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select data-testid="intel-filter-event-type" value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="bg-ivory border border-champagne rounded-full h-9 px-3 text-[13px] text-cocoa outline-none focus:border-mauve">
            {EVENT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select data-testid="intel-filter-valence" value={valence}
            onChange={(e) => setValence(e.target.value)}
            className="bg-ivory border border-champagne rounded-full h-9 px-3 text-[13px] text-cocoa outline-none focus:border-mauve">
            <option value="">All emotional tones</option>
            <option value="positive">Positive</option>
            <option value="negative">Negative</option>
            <option value="neutral">Neutral</option>
          </select>
          <select data-testid="intel-sort" value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-ivory border border-champagne rounded-full h-9 px-3 text-[13px] text-cocoa outline-none focus:border-mauve">
            <option value="recent">Most recent</option>
            <option value="significance">Most significant</option>
          </select>
        </div>

        {events.length === 0 ? (
          <div className="ss-card p-6 text-cocoa/60 text-sm">No events match. Try clearing filters.</div>
        ) : (
          <ul className="space-y-2">
            {events.map((e) => (
              <li key={e.id} data-testid={`intel-event-${e.id}`} className="ss-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <ValenceIcon valence={e.emotional_valence} />
                      <span className="font-medium text-cocoa text-[13px]">{fmtType(e.event_type)}</span>
                      {(e.recurrence_index || 0) > 1 && (
                        <span className="text-[10px] uppercase tracking-wide text-mauve bg-mauve/10 px-2 py-0.5 rounded-full">recurrence #{e.recurrence_index}</span>
                      )}
                      {e.archetype_key && (
                        <span className="text-[10px] uppercase tracking-wide text-cocoa/55">{fmtType(e.archetype_key)}</span>
                      )}
                      {e.profile_id && (
                        <span className="text-[10px] uppercase tracking-wide text-cocoa/55"><Maybe>{aliasOf(e.profile_id)}</Maybe></span>
                      )}
                    </div>
                    <Maybe className="block text-cocoa/85 text-sm leading-relaxed">{e.description}</Maybe>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[11px] text-cocoa/55">{fmtDate(e.occurred_at)}</p>
                    <div className="mt-1"><ScoreBar score={e.significance_score} /></div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Pagination */}
        {eventsTotal > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4">
            <button data-testid="intel-page-prev"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="text-sm text-cocoa/70 hover:text-cocoa disabled:opacity-30 px-3 py-1.5">← Previous</button>
            <span className="text-[11px] text-cocoa/55">{page + 1} / {totalPages}</span>
            <button data-testid="intel-page-next"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="text-sm text-cocoa/70 hover:text-cocoa disabled:opacity-30 px-3 py-1.5">Next →</button>
          </div>
        )}
      </section>

      {/* ─── Summary history ─── */}
      <section className="mb-10" data-testid="intel-summaries-section">
        <div className="flex items-end justify-between mb-3">
          <div>
            <p className="text-[10px] tracking-[0.4em] uppercase text-mauve">Summaries</p>
            <h2 className="font-heading text-2xl sm:text-3xl text-cocoa">Rolling intelligence, versioned.</h2>
          </div>
          <span className="text-[11px] text-cocoa/55">{summaries.length} versions</span>
        </div>
        {summaries.length === 0 ? (
          <div className="ss-card p-6 text-cocoa/60 text-sm">No summaries yet. They regenerate every few advisor sessions.</div>
        ) : (
          <ul className="space-y-2">
            {summaries.map((s) => {
              const open = openSummary === s.id;
              const isCurrent = !s.superseded_by;
              return (
                <li key={s.id} data-testid={`intel-summary-${s.id}`} className="ss-card overflow-hidden">
                  <button onClick={() => setOpenSummary(open ? null : s.id)}
                    className="w-full text-left p-4 flex items-center gap-3 hover:bg-champagne/30">
                    {open ? <ChevronDown size={16} className="text-cocoa/60" /> : <ChevronRight size={16} className="text-cocoa/60" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-cocoa text-sm">Version {s.version}</p>
                        {isCurrent && <span className="text-[10px] uppercase tracking-wide text-sage bg-sage/15 px-2 py-0.5 rounded-full">current</span>}
                        {!isCurrent && <span className="text-[10px] uppercase tracking-wide text-cocoa/45">superseded</span>}
                      </div>
                      <p className="text-[11px] text-cocoa/55 mt-0.5">{fmtDate(s.generated_at)} · covers {s.events_covered} events</p>
                    </div>
                    <Clock size={14} className="text-cocoa/40" />
                  </button>
                  {open && (
                    <div className="px-5 pb-5 pt-1 border-t border-champagne">
                      <Maybe className="block whitespace-pre-wrap text-[13px] text-cocoa/80 leading-relaxed font-mono">{s.body}</Maybe>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ─── Privacy reassurance footer ─── */}
      <div className="mt-12 ss-card-premium p-6 text-center">
        <p className="text-[10px] tracking-[0.4em] uppercase text-mauve mb-2">Your IP</p>
        <p className="font-heading italic text-xl text-cocoa">
          Everything on this page is yours alone — searchable, exportable, deletable.
        </p>
        <p className="text-[12px] text-cocoa/60 mt-2">
          Significance, recurrence, and continuity are computed by deterministic platform code in MongoDB —
          not by the language model. They survive any provider swap.
        </p>
        <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/account" data-testid="intel-footer-account" className="text-[12px] underline text-mauve hover:text-cocoa">Export / delete in Account</Link>
          <span className="text-cocoa/30">·</span>
          <Link to="/ask" data-testid="intel-footer-ask" className="text-[12px] underline text-mauve hover:text-cocoa">Ask another question</Link>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, sub, icon, testid }) {
  return (
    <div data-testid={testid} className="ss-card-premium p-4">
      <div className="flex items-center gap-2 text-mauve mb-1">
        {icon}<span className="text-[10px] tracking-[0.3em] uppercase">{label}</span>
      </div>
      <p className="font-heading text-2xl text-cocoa leading-none">{value}</p>
      {sub && <p className="text-[11px] text-cocoa/55 mt-1.5">{sub}</p>}
    </div>
  );
}
